import { Component, HostBinding, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TerritoryEntityType } from '../../../database/models/Territory';
import { AccountStateService } from '../../../services/account-state.service';
import { LayoutService } from '../../../services/layout.service';
import { MapService } from '../../../services/map.service';
import { ClustersDevicesLayer } from '../../../services/map/cluster-devices-layer.service';
import { DevicesLayerService } from '../../../services/map/devices-layer.service';
import { PolygonsDevicesLayer } from '../../../services/map/polygon-devices-layer.service';
import { RoutingService } from '../../../services/routing.service';
import { BaseMap } from '../../../utils/map-data';
import { ALARMS } from '../../management-tab/alarms-sub-tab/alarms-routing.constants';
import { GEOINTELLIGENCE } from '../../statistics-tab/statistics-routing.constants';

@Component({
  selector: 'navigation-control',
  templateUrl: './navigation-control.component.html',
  styleUrls: ['./navigation-control.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconModule, MatButtonModule, TranslatePipe, MatTooltipModule],
})
export class NavigationControlComponent implements OnDestroy {
  public mapService = inject(MapService);
  public routingService = inject(RoutingService);
  private _accountStateService = inject(AccountStateService);
  public layoutService = inject(LayoutService);
  public _devicesLayerService = inject(DevicesLayerService);
  public _polygonsDevicesLayer = inject(PolygonsDevicesLayer);
  public _clustersDevicesService = inject(ClustersDevicesLayer);
  public router = inject(Router);
  public EntityType = TerritoryEntityType;

  public $selectedEntity = this.mapService.$selectedTerritoryType;

  public $isDisplayEcoEvent = this.mapService.$isDisplayEcoEvent;
  public $statePois = this.mapService.$stateDisplayPois;
  public $isDisplayZone = this.mapService.$isDisplayArea;
  public $isMeasuring = this.mapService.$measureMapDistance;
  public $distance = this.mapService.$distanceValue;
  public $isOpenControl = this.mapService.$showMapControl;
  public $isMobile = this.routingService.$isMobile;
  public $account = this._accountStateService.$account;

  private $currentPath = signal(this.router.url.split('?')[0]);
  private destroy$ = new Subject<void>();

  constructor() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e: NavigationEnd) => {
        this.$currentPath.set(e.url.split('?')[0]);
      });
  }
  // Commented waiting approbation of clusters...
  // public $isNotDisplayDevices = computed(() => {
  //   return (
  //     !['/mobility/missions', '/statistics/geointelligence'].includes(this.$currentPath()) &&
  //     !this.startsWithHistoryRoute()
  //   );
  // });

  public readonly ALARMS = ALARMS;

  @HostBinding('style.--marginTop') get marginTop() {
    return this.layoutService.topMapOffset() + 116 + 'px';
  }

  public toggleMapControl() {
    this.mapService.toggleMapControl();
  }

  public toggleClustersMode(event) {
    this._clustersDevicesService.hide();
    this._devicesLayerService.hide();
    this._polygonsDevicesLayer.hide();

    const mode = event.currentTarget.id;

    this.mapService.setClusterMode(mode);

    // this block is repeated on multiple components, but normally disappear with next big devices layer service.
    switch (mode) {
      case 'inactive':
        this._devicesLayerService.show();
        break;
      case 'polygon':
        this._polygonsDevicesLayer.show();
        break;
      case 'circle':
        this._clustersDevicesService.show();
        break;
    }
  }

  public getMyPosition() {
    this.mapService.controlPos();
  }

  public setBaseMap(map: BaseMap) {
    this.mapService.setPreferences([{ data: map, item: 'basemap' }]);

    this.mapService.setBaseMap(map);
  }

  public toggleEcoEventOnMap() {
    this.mapService.toggleDisplayEcoEvents(!this.mapService.$isDisplayEcoEvent());
  }

  public switchDeviceLabel(type: 'registration' | 'driver') {
    this.mapService.deviceLabel[type === 'registration' ? 'reg' : 'driver'] =
      !this.mapService.deviceLabel[type === 'registration' ? 'reg' : 'driver'];

    this._devicesLayerService.toggleLabel();
    this._clustersDevicesService.toggleLabel();
    this._polygonsDevicesLayer.toggleLabel();
  }

  public switchPoisMap(state: 'poiLayer' | 'poiLayerZoomed' | 'none') {
    this.mapService.switchDisplayLandmarkPoint(
      state,
      [GEOINTELLIGENCE as string].includes(this.router.routerState.snapshot.url) ? 'get' : ''
    );
  }

  public toggleZoneOnMap() {
    this.mapService.switchDisplayAreas(
      !this.mapService.$isDisplayArea(),
      [GEOINTELLIGENCE as string].includes(this.router.routerState.snapshot.url) ? 'get' : ''
    );
  }

  public cleanMeasure() {
    this.mapService.refreshLength();
    this.mapService.openMapMeasure();
  }

  public toggleTrackingDevices() {
    this.mapService.$devicesTracking.set(!this.mapService.$devicesTracking());
  }

  public toggleFullScreen() {
    this.layoutService.hasOpenedLeftPanel.set(!this.layoutService.hasOpenedLeftPanel());
  }

  public startsWithHistoryRoute(): boolean {
    return this.routingService.isHistoryMode();
  }

  public displayTerritories(entity?: TerritoryEntityType) {
    this.mapService.displayTerritoriesByEntity(entity);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
